import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { getPlural } from 'lib/pluralize'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'

import RoomOptionsList from './RoomOptionsList'

const RoomOptionsContainer = styled.div`
  > * + * {
    margin-top: ${rem(12)};
  }
`

const TotalPriceContainer = styled.div`
  flex-grow: 1;
`

interface Props {
  activeOccupantsIndex: number
  hasAllOccupanciesOptionsSelected: boolean
  isInEditMode: boolean
  isOpen: boolean
  occupanciesOptions: Map<App.Occupants, Map<Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>
  occupanciesTotalPrice: number
  selectableOccupancies: Array<App.Occupants>
  selectedPurchasableOptions: Map<App.Occupants, Tours.V2OfferPurchasableOption>
  onActiveOccupantsIndexChange: (activeOccupantsIndex: number | 'next') => void
  onAfterClose: () => void
  onClose: () => void
  onOccupantsPurchasableOptionSelect: (
    occupants: App.Occupants,
    purchasableOption: Tours.V2OfferPurchasableOption,
  ) => void
  onSave: () => void
  onToggleEditMode: (isInEditMode: boolean) => void
}

function RoomSelectionModal(props: Props) {
  const {
    activeOccupantsIndex,
    hasAllOccupanciesOptionsSelected,
    isInEditMode,
    isOpen,
    occupanciesOptions,
    occupanciesTotalPrice,
    selectableOccupancies,
    selectedPurchasableOptions,
    onActiveOccupantsIndexChange,
    onAfterClose,
    onClose,
    onOccupantsPurchasableOptionSelect,
    onSave,
    onToggleEditMode,
  } = props

  const handleOccupantsPurchasableOptionSelect = useCallback((
    occupants: App.Occupants,
    purchasableOptions: Tours.V2OfferPurchasableOption,
  ) => {
    onOccupantsPurchasableOptionSelect(occupants, purchasableOptions)
    onActiveOccupantsIndexChange('next')
  }, [onOccupantsPurchasableOptionSelect, onActiveOccupantsIndexChange])

  const handleOccupantsPurchasableOptionUnselect = useCallback((occupants: App.Occupants) => {
    onOccupantsPurchasableOptionSelect(occupants, null)
  }, [onOccupantsPurchasableOptionSelect])

  const handleOccupantsOptionsEdit = useCallback((activeOccupantsIndex: number) => {
    onToggleEditMode(true)
    onActiveOccupantsIndexChange(activeOccupantsIndex)
  }, [onActiveOccupantsIndexChange, onToggleEditMode])

  const handleSave = useCallback(() => {
    onToggleEditMode(false)
    onSave()
  }, [onSave, onToggleEditMode])

  return (
    <ModalBase
      isOpen={isOpen}
      size="small"
      onAfterClose={onAfterClose}
      onClose={onClose}
    >
      <ModalHeader
        title={`Select your room ${getPlural('type', occupanciesOptions.size)}`}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent>
          <RoomOptionsContainer>
            {Array.from(occupanciesOptions.entries()).map(([occupants, options], index) => <RoomOptionsList
              key={index}
              isActive={occupants === selectableOccupancies[activeOccupantsIndex]}
              isEditable={options.size > 1}
              occupants={occupants}
              occupantsIndex={index}
              options={options}
              selectedPurchasableOption={selectedPurchasableOptions.get(occupants)}
              onOccupantsOptionsEdit={handleOccupantsOptionsEdit}
              onOccupantsPurchasableOptionSelect={handleOccupantsPurchasableOptionSelect}
              onOccupantsPurchasableOptionUnselect={handleOccupantsPurchasableOptionUnselect}
            />)}
          </RoomOptionsContainer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Save and Continue',
          disabled: !hasAllOccupanciesOptionsSelected,
          'aria-label': 'save selected rooms and continue to checkout',
          onClick: handleSave,
        }}
        isPrimaryActionHidden={!isInEditMode}
      >
        <TotalPriceContainer>
          <Caption variant="large">Total&nbsp;</Caption>
          <BodyText variant="large" weight="bold">
            <FormatCurrency value={occupanciesTotalPrice} />
          </BodyText>
        </TotalPriceContainer>
      </ModalFooter>
    </ModalBase>
  )
}

export default RoomSelectionModal
